<div
  v-if="is_checkout && active_delivery_type === 'delivery' && can_show_saved_addresses"
  class="border rounded-4 bg-white p-3 mt-3"
>
  <div class="d-flex justify-content-between align-items-start gap-3 mb-3">
    <div>
      <h6 class="fw-bold text-dark mb-1">Delivery Address</h6>
      <p class="text-muted small mb-0">Choose a saved address or add your own delivery address.</p>
    </div>
    <button
      type="button"
      class="btn btn-outline-primary btn-sm rounded text-nowrap"
      :disabled="saved_address_saving"
      @click="save_user_address"
    >
      <span v-if="saved_address_saving" class="spinner-border spinner-border-sm me-1" role="status"></span>
      Save address
    </button>
  </div>

  <div v-if="saved_addresses_loading" class="text-muted small mb-3">
    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
    Loading saved addresses...
  </div>

  <div v-else-if="saved_user_addresses.length" class="mb-3">
    <div class="text-muted small mb-2">Saved addresses</div>
    <div class="list-group">
      <div
        v-for="address in saved_user_addresses"
        :key="get_user_address_id(address) || get_user_address_label(address)"
        class="list-group-item py-2"
        :class="{ active: selected_saved_address_id === get_user_address_id(address) }"
      >
        <div class="d-flex justify-content-between align-items-start gap-2">
          <button
            type="button"
            class="btn p-0 border-0 bg-transparent text-start flex-grow-1"
            :class="selected_saved_address_id === get_user_address_id(address) ? 'text-white' : 'text-dark'"
            @click="select_user_address(address)"
          >
            <span class="fw-semibold d-block">
              {{ address.contact_name || address.name || 'Saved address' }}
            </span>
            <span class="small d-block">
              {{ get_user_address_label(address) }}
            </span>
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="selected_saved_address_id === get_user_address_id(address) ? 'btn-outline-light' : 'btn-outline-danger'"
            :disabled="deleting_address_id === get_user_address_id(address)"
            @click.stop="delete_user_address(address)"
          >
            <span v-if="deleting_address_id === get_user_address_id(address)" class="spinner-border spinner-border-sm" role="status"></span>
            <span v-else>Delete</span>
          </button>
        </div>
      </div>
    </div>
  </div>

  <div class="row g-2">
    <div class="col-12 col-md-6">
      <input v-model="delivery_address_form.contact_name" @input="emit_delivery_address" type="text" class="form-control form-control-sm" :class="get_field_input_class('contact_name')" placeholder="Contact name" />
      <div v-if="get_field_error('contact_name')" class="invalid-feedback d-block">
        {{ get_field_error('contact_name') }}
      </div>
    </div>
    <div class="col-12 col-md-6">
      <input v-model="delivery_address_form.contact_phone" @input="emit_delivery_address" type="text" class="form-control form-control-sm" :class="get_field_input_class('contact_phone')" placeholder="Contact number" />
      <div v-if="get_field_error('contact_phone')" class="invalid-feedback d-block">
        {{ get_field_error('contact_phone') }}
      </div>
    </div>
    <div class="col-12">
      <input v-model="delivery_address_form.email" @input="emit_delivery_address" type="email" class="form-control form-control-sm" :class="get_field_input_class('email')" placeholder="Email address" />
      <div v-if="get_field_error('email')" class="invalid-feedback d-block">
        {{ get_field_error('email') }}
      </div>
    </div>
    <div class="col-12">
      <input
        v-model="delivery_address_form.street_address"
        type="text"
        class="form-control form-control-sm"
        :class="get_field_input_class('street_address')"
        placeholder="Street address"
        autocomplete="off"
        @focus="init_delivery_address_autocomplete"
        @input="handle_delivery_address_input"
      />
      <div v-if="get_field_error('street_address')" class="invalid-feedback d-block">
        {{ get_field_error('street_address') }}
      </div>
    </div>
    <div class="col-12 col-md-6">
      <input v-model="delivery_address_form.suburb" @input="emit_delivery_address" type="text" class="form-control form-control-sm" :class="get_field_input_class('suburb')" placeholder="Suburb / local area" />
      <div v-if="get_field_error('suburb')" class="invalid-feedback d-block">
        {{ get_field_error('suburb') }}
      </div>
    </div>
    <div class="col-12 col-md-6">
      <input v-model="delivery_address_form.city" @input="emit_delivery_address" type="text" class="form-control form-control-sm" :class="get_field_input_class('city')" placeholder="City" />
      <div v-if="get_field_error('city')" class="invalid-feedback d-block">
        {{ get_field_error('city') }}
      </div>
    </div>
    <div class="col-12 col-md-6">
      <input v-model="delivery_address_form.province" @input="emit_delivery_address" type="text" class="form-control form-control-sm" :class="get_field_input_class('province')" placeholder="Province" />
      <div v-if="get_field_error('province')" class="invalid-feedback d-block">
        {{ get_field_error('province') }}
      </div>
    </div>
    <div class="col-12 col-md-6">
      <input v-model="delivery_address_form.postal_code" @input="emit_delivery_address" type="text" class="form-control form-control-sm" :class="get_field_input_class('postal_code')" placeholder="Postal code" />
      <div v-if="get_field_error('postal_code')" class="invalid-feedback d-block">
        {{ get_field_error('postal_code') }}
      </div>
    </div>
  </div>
</div>

<div
  v-else-if="is_checkout && active_delivery_type === 'delivery'"
  class="border rounded bg-white p-3 mt-3"
>
  <div class="mb-3">
    <h6 class="fw-bold text-dark mb-1">Delivery Address</h6>
    <p class="text-muted small mb-0">Delivery details for this order.</p>
  </div>

  <div class="row g-2 small">
    <div class="col-12 col-md-6">
      <span class="text-muted d-block">Contact name</span>
      <strong class="text-break">{{ delivery_address_form.contact_name || 'N/A' }}</strong>
    </div>
    <div class="col-12 col-md-6">
      <span class="text-muted d-block">Contact number</span>
      <strong class="text-break">{{ delivery_address_form.contact_phone || 'N/A' }}</strong>
    </div>
    <div class="col-12">
      <span class="text-muted d-block">Email address</span>
      <strong class="text-break">{{ delivery_address_form.email || 'N/A' }}</strong>
    </div>
    <div class="col-12">
      <span class="text-muted d-block">Street address</span>
      <strong class="text-break">{{ delivery_address_form.street_address || 'N/A' }}</strong>
    </div>
    <div class="col-12 col-md-6">
      <span class="text-muted d-block">Suburb / local area</span>
      <strong class="text-break">{{ delivery_address_form.suburb || 'N/A' }}</strong>
    </div>
    <div class="col-12 col-md-6">
      <span class="text-muted d-block">City</span>
      <strong class="text-break">{{ delivery_address_form.city || 'N/A' }}</strong>
    </div>
    <div class="col-12 col-md-6">
      <span class="text-muted d-block">Province</span>
      <strong class="text-break">{{ delivery_address_form.province || 'N/A' }}</strong>
    </div>
    <div class="col-12 col-md-6">
      <span class="text-muted d-block">Postal code</span>
      <strong class="text-break">{{ delivery_address_form.postal_code || 'N/A' }}</strong>
    </div>
  </div>
</div>
